/**
 * El Clasico 2026 - Real Madrid vs FC Barcelona
 * Liga Endesa (ACB) - January 4, 2026
 * The Wire Report Pre-loaded Game Data
 */

export const gameMetadata = {
  fixture: "Real Madrid vs FC Barcelona",
  competition: "Liga Endesa (ACB)",
  date: "2026-01-04",
  finalScore: "Real Madrid 98 - 105 FC Barcelona",
  venue: "WiZink Center (Madrid)",
  analysisScope: "Full Biomechanical Video Review + Bilateral Synergy Data Integration",
  dataFidelity: "Complete play-by-play mapping for both offensive and defensive phases.",
  videoUrl: null, // Will be set when video is available
  thumbnailUrl: "/images/el-clasico-thumb.jpg"
};

// Key moments with timestamps for video markers
export const keyMoments = [
  {
    id: "km-001",
    timeVideo: "00:05",
    timeSeconds: 5,
    event: "Campazzo P&R Assist",
    category: "playmaking",
    team: "Real Madrid",
    player: "Facundo Campazzo",
    analysis: "Sets tempo early. Demonstrates Real's reliance on Campazzo's creation. Elite hip dissociation allows him to change direction without losing velocity.",
    confidence: 0.92,
    icon: "assist"
  },
  {
    id: "km-002",
    timeVideo: "00:25",
    timeSeconds: 25,
    event: "Hezonja Catch-and-Shoot",
    category: "shooting",
    team: "Real Madrid",
    player: "Mario Hezonja",
    analysis: "Significant vertical generation on catch-and-shoot, indicating healthy lower body power generation. High set point, textbook 'dip and drive' mechanics.",
    confidence: 0.88,
    icon: "shooting"
  },
  {
    id: "km-003",
    timeVideo: "00:54",
    timeSeconds: 54,
    event: "Satoransky P&R Drive",
    category: "drive",
    team: "FC Barcelona",
    player: "Tomas Satoransky",
    analysis: "Long, efficient strides in P&R penetration. Maintains low center of gravity. Minimal knee valgus during change of direction, indicating strong hip stability.",
    confidence: 0.95,
    icon: "drive"
  },
  {
    id: "km-004",
    timeVideo: "01:29",
    timeSeconds: 89,
    event: "Punter Off-Screen Shot",
    category: "shooting",
    team: "FC Barcelona",
    player: "Kevin Punter",
    analysis: "Two-motion shot with high release. Slight valgus collapse on landing during contested attempts. Tight curling radius around pin-downs, minimizing time to release.",
    confidence: 0.85,
    icon: "shooting"
  },
  {
    id: "km-005",
    timeVideo: "03:52",
    timeSeconds: 232,
    event: "Rebounding Breakdown",
    category: "rebounding",
    team: "FC Barcelona",
    analysis: "Real's box-out discipline is low when Tavares contests shots. Barcelona should continue crashing corners for offensive rebounding opportunities.",
    confidence: 0.78,
    icon: "rebound"
  },
  {
    id: "km-006",
    timeVideo: "04:48",
    timeSeconds: 288,
    event: "Shengelia Post Up",
    category: "post",
    team: "FC Barcelona",
    player: "Tornike Shengelia",
    analysis: "Shengelia used superior lower body strength to displace Deck. Elite power generation from the glutes in post-ups. CSV Row 55 confirms Post Up execution.",
    confidence: 0.91,
    icon: "post"
  },
  {
    id: "km-007",
    timeVideo: "06:34",
    timeSeconds: 394,
    event: "Transition Defense Breakdown",
    category: "defense",
    team: "Real Madrid",
    analysis: "Poor transition defense identification, allowing Barcelona open spot-up 3s. Real did not match up, leaving Marcos wide open. Shifted momentum.",
    confidence: 0.89,
    icon: "defense"
  },
  {
    id: "km-008",
    timeVideo: "11:20",
    timeSeconds: 680,
    event: "Bench Frustration",
    category: "psychology",
    team: "Real Madrid",
    analysis: "Players (Llull, Rodriguez) seen arguing coverage on the bench. Visible frustration signals indicating communication breakdown.",
    confidence: 0.72,
    icon: "psychology"
  },
  {
    id: "km-009",
    timeVideo: "14:47",
    timeSeconds: 887,
    event: "Hezonja 3pt Response",
    category: "clutch",
    team: "Real Madrid",
    player: "Mario Hezonja",
    analysis: "Critical momentum stopper. Biomechanically perfect 'sway' on the shot. Textbook hip alignment in mid-air, allowing immediate energy transfer upon landing.",
    confidence: 0.96,
    icon: "clutch"
  },
  {
    id: "km-010",
    timeVideo: "17:45",
    timeSeconds: 1065,
    event: "Hezonja Elite Deceleration",
    category: "biomechanics",
    team: "Real Madrid",
    player: "Mario Hezonja",
    analysis: "Elite stopping power in transition to transition into pull-up. Showcased versatility - on drives, utilizes broad jump mechanics to finish over length.",
    confidence: 0.87,
    icon: "biomechanics"
  },
  {
    id: "km-011",
    timeVideo: "17:50",
    timeSeconds: 1070,
    event: "Campazzo Fatigue Indicators",
    category: "fatigue",
    team: "Real Madrid",
    player: "Facundo Campazzo",
    analysis: "Reduced defensive stance depth in Q4, reaching instead of sliding. Video shows degradation in stride frequency indicating biomechanical fatigue.",
    confidence: 0.83,
    icon: "fatigue"
  },
  {
    id: "km-012",
    timeVideo: "19:15",
    timeSeconds: 1155,
    event: "Laprovittola Dagger 3pt",
    category: "clutch",
    team: "FC Barcelona",
    player: "Nicolas Laprovittola",
    analysis: "Broken play execution (ISO). Defender (Tavares) dropped too deep, failed to contest high release point. The biomechanical breakdown of Tavares vs. Laprovittola's quick release. The defining tactical mismatch of the game.",
    confidence: 0.98,
    icon: "dagger"
  },
  {
    id: "km-013",
    timeVideo: "20:05",
    timeSeconds: 1205,
    event: "Defensive Collapse",
    category: "defense",
    team: "Real Madrid",
    analysis: "Visible head drops after missed defensive rotations. Chus Mateo displaying static posture during opponent runs. Real Madrid defense collapsed into the paint on a drive, over-helping.",
    confidence: 0.85,
    icon: "defense"
  },
  {
    id: "km-014",
    timeVideo: "20:45",
    timeSeconds: 1245,
    event: "Missed Call - Officiating",
    category: "officiating",
    team: "FC Barcelona",
    analysis: "Potential missed contact on Laprovittola drive. Ref had obstructed view from slot. High confidence based on clear view from baseline camera.",
    confidence: 0.90,
    icon: "whistle"
  }
];

// Player biomechanical analysis
export const playerAnalysis = [
  {
    player: "Facundo Campazzo",
    team: "Real Madrid",
    role: "Primary Creator / Point of Attack",
    image: "/images/players/campazzo.jpg",
    biomechanics: {
      acceleration: "Elite hip dissociation allows him to change direction without losing velocity. However, video at 55:31 shows a degradation in stride frequency during the turnover, indicating biomechanical fatigue.",
      deceleration: "Uses a 'negative step' braking mechanism to freeze drop defenders, creating windows for the pass or pull-up.",
      changeOfDirection: "Elite lateral deceleration. Hip swivel speed allows passing angles against hedging bigs."
    },
    tacticalImpact: "High-risk, high-reward. Generated significant perimeter looks, but the CSV reveals a correlation between high-usage P&R sets and turnovers in clutch moments.",
    fatigueStatus: "Degraded mechanics in Q4",
    csvReference: "RM_CSV Rows 1, 21, 23, 91, 97, 103, 130, 193, 205"
  },
  {
    player: "Mario Hezonja",
    team: "Real Madrid",
    role: "Hybrid Scoring Wing / Stretch 4",
    image: "/images/players/hezonja.jpg",
    biomechanics: {
      kineticChain: "Showcased elite versatility. On Pick & Pops, his hip alignment in mid-air is flawless, allowing immediate energy transfer upon landing. On drives, utilizes broad jump mechanics to finish over length.",
      shootingMechanics: "Textbook 'dip and drive' mechanics on the catch. Generates significant torque from the hips on contested 3s.",
      landingMechanics: "Tendency to land with feet split (right forward) on corner 3s, potentially indicating a hip alignment preference."
    },
    tacticalImpact: "Real's most efficient mismatch weapon. He punished Barcelona's switches both in the post and on the perimeter. His ability to crash the offensive glass was a critical biomechanical advantage.",
    fatigueStatus: "Stable mechanics, but defensive stance shallowed in Q4",
    csvReference: "RM_CSV Rows 4, 8, 9, 14, 29, 77, 84, 116, 120, 140, 209, 214, 227"
  },
  {
    player: "Tomas Satoransky",
    team: "FC Barcelona",
    role: "Primary Ball Handler / P&R Initiator",
    image: "/images/players/satoransky.jpg",
    biomechanics: {
      driveMechanics: "High stride efficiency. Uses long, loping strides to eat space in drop coverage. Maintains a neutral spine angle during drives, allowing for superior vision over defense.",
      deceleration: "Controlled two-foot stops in the paint to pivot and find shooters.",
      shootingMechanics: "Minimal vertical lift on P&R pull-ups. Relies on high release point (6'7\" frame) rather than jump height. Consistent 'dip' into the shot pocket implies rhythm dependency."
    },
    tacticalImpact: "Systematically dismantled Real's drop coverage. His refusal to over-penetrate kept Tavares stuck in the paint, opening the mid-range.",
    fatigueStatus: "Low. Mechanics remained consistent through Q4",
    csvReference: "BAR_CSV Rows 10, 26, 48, 76, 87, 137"
  },
  {
    player: "Kevin Punter",
    team: "FC Barcelona",
    role: "Off-Screen Shooter / Secondary Creator",
    image: "/images/players/punter.jpg",
    biomechanics: {
      offBallMovement: "Elite kinetic linking. He turns his hips mid-air while curling off pin-downs to square shoulders to the rim before landing.",
      shotRelease: "High set point with a slight backward sway (sweep and sway) to create separation from trailing defenders.",
      form: "Two-motion shot with high release. Slight valgus collapse on landing during contested attempts."
    },
    tacticalImpact: "High efficiency on plays moving to his right. Barcelona ran specific 'Floppy' sets to get him curling towards the sideline.",
    fatigueStatus: "Landing mechanics showed degradation in late Q4, posing injury risk",
    csvReference: "BAR_CSV Rows 13, 24, 30"
  },
  {
    player: "Tornike Shengelia",
    team: "FC Barcelona",
    role: "Point Forward / Post",
    image: "/images/players/shengelia.jpg",
    biomechanics: {
      forceGeneration: "Elite power generation from the glutes in post-ups. Displaces defenders (Deck/Yabusele) purely with lower body torque.",
      pivotEfficiency: "Uses aggressive front pivots to face up, threatening the drive to open passing lanes.",
      postFootwork: "Heavy reliance on the 'drop step' to the baseline."
    },
    tacticalImpact: "Used as a short-roll playmaker effectively. CSV Row 39 confirms 'P&R Roll Man > Make 2 Pts'.",
    fatigueStatus: "Consistent power output throughout",
    csvReference: "BAR_CSV Rows 55, 69, 152"
  },
  {
    player: "Walter Tavares",
    team: "Real Madrid",
    role: "Vertical Spacer / Anchor",
    image: "/images/players/tavares.jpg",
    biomechanics: {
      groundReaction: "Struggles with lateral change of direction (defense), but generates immense vertical force with minimal load time (offense).",
      screenSetting: "Wide base provides massive surface area for P&R ball handlers. Contact absorption is elite.",
      mobility: "Cannot biomechanically recover to shooters like Laprovittola/Parra once committed to the paint."
    },
    tacticalImpact: "Offensively efficient as a roller, but defensively exploited by Barcelona's spacing.",
    fatigueStatus: "Mobility limitations exposed in Q4",
    csvReference: "RM_CSV Rows 16, 27, 78, 80, 112, 132"
  }
];

// Tactical breakdown
export const tacticalBreakdown = {
  barcelonaOffense: {
    scheme: "Spread P&R with Weakside Lift",
    primaryAction: "High Pick & Roll / Spread P&R",
    execution: "Satoransky/Laprovittola rejecting screens to attack baseline or pulling up against drop coverage.",
    efficiency: "High. Created consistent 2v1 situations on the weak side.",
    dataEvidence: "High volume of 'P&R BH' tags (Rows 5, 10, 26, 48, 52) confirms video observation.",
    counterAdjustment: "When Real helped off corners, Barca found Parra/Marcos for open 3s."
  },
  realMadridOffense: {
    scheme: "Horns & High P&R Variability",
    execution: "Heavy reliance on Campazzo creating off the dribble. When stalled, offense devolved into Hezonja/Lyles ISOs.",
    efficiencyGap: "Excellent cut execution (Rows 27, 44, 132 all 'Make 2 Pts'). Real should have utilized cutting actions more over stagnant ISOs.",
    weakness: "Perimeter shooting consistency from secondary handlers. Llull and Campazzo missed several P&R 3s which killed momentum."
  },
  realMadridDefense: {
    scheme: "Drop Coverage (Tavares) / Switch (Perimeter)",
    breakdown: "Guards struggled to navigate screens, leaving Tavares in 'no man's land' against pull-up shooters.",
    transition: "Poor transition defense identification, allowing Barcelona open spot-up 3s."
  },
  barcelonaDefense: {
    scheme: "Switching / Ice on Side P&Rs",
    primaryStrategy: "Aggressive switching 1-through-4 with Veselý protecting the rim. Grimau signaled 'Ice' coverage on side pick-and-rolls.",
    perimeterDefense: "Physical contests on catch-and-shoot attempts. Forced Real into contested mid-range shots.",
    rotations: "Quick weak-side rotations to contest corner 3s. Barcelona closed out well on secondary shooters.",
    rebounding: "Crashed offensive glass aggressively when Tavares contested shots. Exploited Real's poor box-out discipline.",
    dataEvidence: "Held Real to 43.5% from three (10/23), below their season average."
  },
  keyMatchup: {
    description: "Shengelia (BAR) vs Deck (RMA)",
    analysis: "Shengelia used superior lower body strength to displace Deck in post-up situations."
  }
};

// Bench dynamics
export const benchDynamics = {
  barcelona: {
    energyCurve: "Ascending",
    engagement: "High. Standing ovations on 3PM. Bench erupted at 19:15 (Laprovittola 3PT). Visible synchronization in clapping/standing during defensive stops.",
    coachSignals: "Roger Grimau maintaining active sideline presence, frequent hand signaling for defensive switches. Constantly signaling 'Ice' on side P&Rs.",
    momentum: "Explosive reaction to Laprovittola's late 3-pointer, indicating psychological edge."
  },
  realMadrid: {
    energyCurve: "Descending/Anxious",
    engagement: "Moderate to Low in Q4. Frustration evident in Q3/Q4.",
    frustrationSignals: "Visible head drops after missed defensive rotations. Chus Mateo crossed arms (defensive posture) during the 4th quarter run. Players (Llull, Rodriguez) seen arguing coverage on the bench.",
    coachStatus: "Chus Mateo observed pacing more frequently in Q4, signaling confusion on P&R coverages."
  }
};

// Officiating analysis
export const officiatingAnalysis = {
  overallGrade: "Loose / Physical",
  contactThreshold: "Inconsistent. Real Madrid drew significant fouls in P&R roll situations, suggesting officials were protecting the roller. However, perimeter contact on Barcelona's shooters was called tighter.",
  positioning: {
    leadOfficial: "Generally good positioning on baseline drives, maintaining open angle.",
    trailOfficial: "Appropriate spacing on perimeter shots."
  },
  callConsistency: {
    contact: "Allowed physical play in the post (Shengelia vs Deck matchups).",
    screenSetting: "Loose interpretation of moving screens on perimeter DHOs."
  },
  missedCalls: [
    {
      timestamp: "20:45",
      description: "Laprovittola drive - contact on arm. No call.",
      confidence: "High (Clear view from baseline camera)"
    },
    {
      timestamp: "04:12",
      description: "Shengelia drives left. Whistle called, but video suggests minimal contact. Makeup call?",
      confidence: "Medium"
    }
  ],
  impact: "Real Madrid benefited from interior foul calls (Tavares/Deck), while Barcelona utilized the loose perimeter officiating to set physical screens."
};

// Recommendations
export const recommendations = [
  {
    team: "Real Madrid",
    focus: "Defensive P&R Coverage",
    priority: "high",
    action: "Implement 'Hedge and Recover' or 'Ice' coverage against Satoransky/Laprovittola to prevent uncontested pull-ups. Drop coverage is statistically failing against their shooting mechanics. Tavares must play at the level of the screen (Hedge) against shooting guards."
  },
  {
    team: "Real Madrid",
    focus: "Offensive Diversity",
    priority: "medium",
    action: "Reduce reliance on Guard ISO/P&R pull-ups. Increase frequency of 'Cut' actions which yielded 100% efficiency in the sampled data. Move the defense side-to-side rather than north-south."
  },
  {
    team: "FC Barcelona",
    focus: "Offensive Rebounding",
    priority: "medium",
    action: "Continue crashing corners. Real's box-out discipline is low when Tavares contests shots."
  },
  {
    team: "FC Barcelona",
    focus: "Rotation Management",
    priority: "low",
    action: "Monitor Kevin Punter's minutes. His biomechanics (landing mechanics) showed degradation in late Q4, posing an injury risk despite high offensive output."
  },
  {
    team: "Officials",
    focus: "Post-Play Monitoring",
    priority: "medium",
    action: "Monitor off-arm usage by Shengelia in post-ups; potential offensive foul initiation."
  }
];

// Translations for multilingual support
export const translations = {
  en: {
    title: "THE WIRE",
    subtitle: "Basketball Intelligence Report",
    fixture: "Fixture",
    finalScore: "Final Score",
    venue: "Venue",
    keyMoments: "Key Moments",
    playerAnalysis: "Player Analysis",
    tacticalBreakdown: "Tactical Breakdown",
    recommendations: "Recommendations",
    benchDynamics: "Bench Dynamics",
    officiating: "Officiating Analysis",
    playVideo: "Play Video",
    pause: "Pause",
    stepForward: "Step Forward",
    stepBack: "Step Back",
    frameByFrame: "Frame by Frame",
    analyzing: "Analyzing...",
    chatPlaceholder: "Ask about the game...",
    send: "Send"
  },
  es: {
    title: "THE WIRE",
    subtitle: "Informe de Inteligencia de Baloncesto",
    fixture: "Partido",
    finalScore: "Resultado Final",
    venue: "Estadio",
    keyMoments: "Momentos Clave",
    playerAnalysis: "Análisis de Jugadores",
    tacticalBreakdown: "Desglose Táctico",
    recommendations: "Recomendaciones",
    benchDynamics: "Dinámica del Banquillo",
    officiating: "Análisis del Arbitraje",
    playVideo: "Reproducir Video",
    pause: "Pausar",
    stepForward: "Avanzar",
    stepBack: "Retroceder",
    frameByFrame: "Cuadro a Cuadro",
    analyzing: "Analizando...",
    chatPlaceholder: "Pregunta sobre el partido...",
    send: "Enviar"
  },
  pt: {
    title: "THE WIRE",
    subtitle: "Relatório de Inteligência de Basquete",
    fixture: "Jogo",
    finalScore: "Placar Final",
    venue: "Arena",
    keyMoments: "Momentos-Chave",
    playerAnalysis: "Análise de Jogadores",
    tacticalBreakdown: "Análise Tática",
    recommendations: "Recomendações",
    benchDynamics: "Dinâmica do Banco",
    officiating: "Análise da Arbitragem",
    playVideo: "Reproduzir Vídeo",
    pause: "Pausar",
    stepForward: "Avançar",
    stepBack: "Voltar",
    frameByFrame: "Quadro a Quadro",
    analyzing: "Analisando...",
    chatPlaceholder: "Pergunte sobre o jogo...",
    send: "Enviar"
  }
};

// Export all data as default
export default {
  gameMetadata,
  keyMoments,
  playerAnalysis,
  tacticalBreakdown,
  benchDynamics,
  officiatingAnalysis,
  recommendations,
  translations
};
